import { Stack, Button, TextField, Grid, CircularProgress, InputAdornment, FormControlLabel, Checkbox, Box } from "@mui/material";
import { useState, useEffect } from "react";
import SearchIcon from "@mui/icons-material/Search";
import { useCheckout } from "../CheckoutContext";
import { buscarCEP } from "../../services/cepService";

export default function AddressStep() {
    const { checkoutData, updateSection, nextStep, prevStep, hasSavedAddresses, user } = useCheckout();
    const [loadingCEP, setLoadingCEP] = useState(false);
    const [lastSearchedCEP, setLastSearchedCEP] = useState("");

    const isValid =
        checkoutData.address.cep &&
        checkoutData.address.street &&
        checkoutData.address.number &&
        checkoutData.address.city &&
        checkoutData.address.state &&
        checkoutData.address.district;

    const textFieldSx = {
        "& .MuiInputLabel-root": {
            color: "#6b7280",
            fontSize: 14,
        },
        "& .MuiOutlinedInput-root": {
            borderRadius: 2,
        },
    };

    // Buscar CEP automaticamente quando tiver 8 dígitos
    useEffect(() => {
      const cep = checkoutData.address.cep || "";

      if (cep.length === 8 && cep !== lastSearchedCEP) {
        buscarEnderecoPorCEP(cep);
      }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [checkoutData.address.cep]);

    async function buscarEnderecoPorCEP(cep) {
        setLoadingCEP(true);
        setLastSearchedCEP(cep);

        const resultado = await buscarCEP(cep);

        if (resultado.ok && resultado.data) {
            updateSection("address", {
                cep: resultado.data.cep,
                street: resultado.data.street,
                district: resultado.data.district,
                city: resultado.data.city,
                state: resultado.data.state,
                // Mantém complemento se já existir, mas não preenche automaticamente
                complement: checkoutData.address.complement || "",
            });
        }

        setLoadingCEP(false);
    }

    return (
        <Stack spacing={2}>
            <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                    <TextField
                      label="CEP"
                      fullWidth
                      value={checkoutData.address.cep || ""}
                      onChange={(e) => {
                        // remove tudo que não for número
                        const apenasNumeros = e.target.value.replace(/\D/g, "");

                        // limita exatamente a 8 caracteres
                        const cepLimitado = apenasNumeros.slice(0, 8);

                        updateSection("address", { cep: cepLimitado });
                      }}
                      placeholder="00000000"
                      inputProps={{
                        maxLength: 8,
                        inputMode: "numeric",
                        pattern: "[0-9]*",
                      }}
                      sx={textFieldSx}
                      InputProps={{
                        endAdornment: loadingCEP ? (
                          <InputAdornment position="end">
                            <CircularProgress size={20} />
                          </InputAdornment>
                        ) : checkoutData.address.cep?.length === 8 ? (
                          <InputAdornment position="end">
                            <SearchIcon color="primary" />
                          </InputAdornment>
                        ) : null,
                      }}
                      helperText={
                        checkoutData.address.cep?.length === 8
                          ? "Endereço encontrado automaticamente"
                          : "Digite o CEP (apenas números)"
                      }
                    />
                </Grid>
                <Grid item xs={12} sm={6}>
                    <TextField
                        label="Rua / Avenida"
                        fullWidth
                        value={checkoutData.address.street}
                        onChange={(e) =>
                            updateSection("address", { street: e.target.value })
                        }
                        sx={textFieldSx}
                    />
                </Grid>
                <Grid item xs={12} sm={4}>
                    <TextField
                        label="Número"
                        fullWidth
                        value={checkoutData.address.number}
                        onChange={(e) =>
                            updateSection("address", { number: e.target.value })
                        }
                        sx={textFieldSx}
                    />
                </Grid>
                <Grid item xs={12} sm={8}>
                    <TextField
                        label="Complemento"
                        fullWidth
                        value={checkoutData.address.complement || ""}
                        onChange={(e) =>
                            updateSection("address", { complement: e.target.value })
                        }
                        sx={textFieldSx}
                    />
                </Grid>
                <Grid item xs={12} sm={4}>
                    <TextField
                        label="Bairro"
                        fullWidth
                        value={checkoutData.address.district || ""}
                        onChange={(e) =>
                            updateSection("address", { district: e.target.value })
                        }
                        sx={textFieldSx}
                    />
                </Grid>
                <Grid item xs={12} sm={4}>
                    <TextField
                        label="Cidade"
                        fullWidth
                        value={checkoutData.address.city || ""}
                        onChange={(e) =>
                            updateSection("address", { city: e.target.value })
                        }
                        sx={textFieldSx}
                    />
                </Grid>
                <Grid item xs={12} sm={4}>
                    <TextField
                        label="Estado"
                        fullWidth
                        value={checkoutData.address.state || ""}
                        onChange={(e) =>
                            updateSection("address", { state: e.target.value })
                        }
                        sx={textFieldSx}
                    />
                </Grid>
            </Grid>

            {/* Opção para salvar como endereço padrão (só aparece se usuário não tiver endereços salvos) */}
            {user && !hasSavedAddresses && (
                <Box sx={{ mt: 2, p: 2, bgcolor: "rgba(0,0,0,0.02)", borderRadius: 2 }}>
                    <FormControlLabel
                        control={
                            <Checkbox
                                checked={checkoutData.address.saveAsDefault || false}
                                onChange={(e) =>
                                    updateSection("address", { saveAsDefault: e.target.checked })
                                }
                            />
                        }
                        label="Salvar este endereço como padrão para próximas compras"
                    />
                </Box>
            )}

            <Stack direction="row" spacing={2} mt={2}>
                <Button variant="outlined" onClick={prevStep} fullWidth>
                    Voltar
                </Button>

                <Button
                    variant="contained"
                    disabled={!isValid}
                    onClick={nextStep}
                    fullWidth
                >
                    Continuar
                </Button>
            </Stack>
        </Stack>
    );
}
